"use client"

import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { Appointment } from "@/lib/types";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Badge } from "@/components/ui/badge";
import { Phone, Mail, Clock } from "lucide-react";
import { computeDurationMinutes, formatDurationMinutes, formatCurrency } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Guard } from "@/components/access/Guard";
import { fetchAppointmentDetails } from "@/lib/services/appointmentModalService";
import type { AppointmentRecord } from "@/lib/types/appointment";
// Centralized cost calculation engine
import { getServiceLineTotal } from "@/lib/appointment/costEngine";
import { useIsMobile } from "@/hooks/use-mobile";


interface AppointmentHoverCardProps {
  appointment: Appointment;
  children: React.ReactNode;
  /** Controlled open state (for mobile tap-to-open) */
  open?: boolean;
  /** Callback when open state changes (for mobile tap-to-open) */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Hover card showing a compact, read-only snapshot of the appointment.
 * On open it will fetch full appointment details (same service used by modal)
 * and prefer the fetched/merged appointment for rendering. Shows a small
 * loading hint while fetching.
 * 
 * On mobile: renders a centered portal-based modal (bypasses Radix positioning).
 * On desktop: uses standard HoverCard with side positioning.
 */
export function AppointmentHoverCard({
  appointment,
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: AppointmentHoverCardProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isMobile = useIsMobile();

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const handleOpenChange = (v: boolean) => {
    if (isControlled && controlledOnOpenChange) {
      controlledOnOpenChange(v);
    } else {
      setInternalOpen(v);
    }
  };
  const { t } = useTranslation();

  const apptId = Number(appointment.id ?? appointment.record_id ?? 0);

  const { data: fetchedAppointment, isLoading: isDetailsLoading } = useQuery<AppointmentRecord | null>({
    queryKey: ["appointmentDetails", apptId],
    queryFn: () => fetchAppointmentDetails(apptId),
    enabled: isOpen && !!apptId,
    staleTime: 60_000, // was 0: re-hover within a minute shouldn't refetch; edits invalidate the key (doc 07 Phase B)
    gcTime: 0,
    refetchOnMount: "always",
    retry: 0,
  });

  // prefer the fetched, merged appointment when available
  const appt: any = fetchedAppointment ?? appointment;

  const clientName =
    typeof appt.client === "string"
      ? appt.client
      : appt.client?.name || appt.client_name || t("common.labels.no_name", "No name");

  const phone = appt.client?.phone || appt.phone || "";
  const email = appt.client?.email || "";

  const success = appt?.clientStats?.success_visits_count ?? 0;
  const fail = appt?.clientStats?.fail_visits_count ?? 0;
  const visits =
    appt?.clientStats?.total_visits_count ??
    (Number.isFinite(success) && Number.isFinite(fail) ? success + fail : success || fail || 0);

  // services (title, cost, amount) - use centralized getServiceLineTotal
  const services: { title: string; amount: number; cost: number }[] =
    Array.isArray(appt.services) && appt.services.length > 0
      ? appt.services.map((s: any) => ({
        title: s.title || t("appointment.details.service", "Service"),
        amount: typeof s.amount === "number" ? s.amount : 1,
        // Use centralized getServiceLineTotal for consistent cost calculation
        cost: getServiceLineTotal(s),
      }))
      : [];

  // Total already accounts for quantity from getServiceLineTotal
  const totalCost: number = services.reduce((sum: number, s) => sum + (s.cost || 0), 0);

  const statusLabel =
    appt.attendance === 1
      ? t("appointment.status.arrived", "Arrived")
      : appt.attendance === -1
        ? t("appointment.status.no_show", "No show")
        : appt.confirmed === 1
          ? t("appointment.status.confirmed", "Confirmed")
          : t("appointment.status.waiting", "Waiting");

  const timeRange = (appt.startTime ? appt.startTime : "") + (appt.endTime ? ` – ${appt.endTime}` : "");

  const durationLabel = (() => {
    if (appt.detailsSection?.serviceDurationString) {
      return appt.detailsSection.serviceDurationString;
    }
    // Prefer deriving duration from the actual start/end times (ground-truth)
    const fromTimes = computeDurationMinutes(appt.startTime, appt.endTime);
    const totalMin = fromTimes ?? (typeof appt.durationMin === "number" ? appt.durationMin : null);
    return formatDurationMinutes(totalMin);
  })();

  // Shared content for both mobile and desktop
  const cardContent = (
    <div className="space-y-3">
      {/* Client header */}
      <div>
        <div className="text-sm font-semibold text-gray-900 leading-tight">{clientName}</div>
        <div className="mt-1 space-y-1 text-xs text-gray-500">
          {phone && (
            <Guard
              check={(p) => p.timetable.phonesVisible}
              mode="render"
              fallback={
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span>••••••</span>
                </div>
              }
            >
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                <span>{phone}</span>
              </div>
            </Guard>
          )}
          {email && (
            <Guard
              check={(p) => p.timetable.phonesVisible}
              mode="render"
              fallback={
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span>••••••</span>
                </div>
              }
            >
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <span>{email}</span>
              </div>
            </Guard>
          )}
          {isDetailsLoading && !fetchedAppointment ? (
            <div className="text-xs text-gray-500 italic">{t("common.labels.loading", "Loading...")}</div>
          ) : null}
        </div>
      </div>

      {/* Client Tags */}
      <div className="flex flex-wrap gap-1 mt-2">
        {appt.detailsSection?.clientTags && appt.detailsSection.clientTags.length > 0 ? (
          appt.detailsSection.clientTags.map((tag: any) => (
            <span
              key={tag.id}
              className="px-2 py-0.5 text-[10px] font-medium rounded"
              style={{
                backgroundColor: tag.color || "#eee",
                color: tag.font_color || "#222",
              }}
            >
              {tag.title}
            </span>
          ))
        ) : null}
      </div>

      {/* Visits + Status */}
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="bg-gray-100 text-gray-800 px-2 py-0.5">
          {t("appointment.details.visits", "Visits:")}{" "}{visits}
        </Badge>
        <Badge variant="secondary" className="bg-gray-100 text-gray-800 px-2 py-0.5">
          {statusLabel}
        </Badge>
      </div>

      {/* Services list */}
      <div>
        <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">{t("appointment.details.service", "Service")}</div>

        {services.length > 0 ? (
          services.map((s, idx) => (
            <div key={idx} className="flex justify-between items-start text-sm mb-1">
              <div className="text-gray-700">
                {s.title}
                {s.amount > 1 && <span className="text-gray-400 ml-1">x{s.amount}</span>}
              </div>
              <div className="font-medium whitespace-nowrap text-sm">{formatCurrency(s.cost)}</div>
            </div>
          ))
        ) : (
          <div className="text-sm text-gray-400 italic">{t("appointment.details.no_services", "No services")}</div>
        )}
      </div>

      {/* Totals */}
      <div className="flex justify-between items-end pt-1">
        <div className="text-sm text-gray-500">{t("common.labels.total", "Total")}</div>
        <div className="text-sm font-semibold text-gray-900">{formatCurrency(totalCost)}</div>
      </div>

      {/* Time / duration + comment */}
      <div className="bg-gray-50 p-3 rounded text-sm text-gray-700">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-400" />
          <div className="flex-1">
            <div className="font-medium text-sm">{timeRange}</div>
            <div className="text-xs text-gray-500">{durationLabel}</div>
          </div>
        </div>

        {appt.comment ? (
          <div className="mt-3">
            <div className="text-xs text-gray-400 mb-1">{t("appointment.details.comment", "Comment")}</div>
            <div className="text-sm text-gray-700 bg-white p-2 rounded border border-gray-100">{appt.comment}</div>
          </div>
        ) : null}
      </div>
    </div>
  );

  // Mobile: render our own portal-based centered modal (bypasses Radix positioning)
  if (isMobile) {
    return (
      <>
        {children}
        {isOpen && createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/40 z-[9998]"
              onClick={() => handleOpenChange(false)}
            />
            {/* Centered modal */}
            <div
              className="fixed z-[9999] w-[90vw] max-w-[320px] p-4 overflow-hidden rounded-xl border shadow-lg bg-white"
              style={{
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                type="button"
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => handleOpenChange(false)}
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              {cardContent}
            </div>
          </>,
          document.body
        )}
      </>
    );
  }

  // Desktop: use standard HoverCard with side positioning
  return (
    <HoverCard open={isOpen} openDelay={200} closeDelay={100} onOpenChange={handleOpenChange}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>

      <HoverCardContent
        className="w-[320px] p-4 overflow-hidden rounded-xl border shadow-lg bg-white z-[9999]"
        align="start"
        side="right"
        sideOffset={8}
      >
        {cardContent}
      </HoverCardContent>
    </HoverCard>
  );
}

